import React, { useMemo } from "react";
import { UseFormRegister, FieldErrors, UseFormWatch } from "react-hook-form";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData, Shipment } from "../../types";
import {
  INCOTERMS_OPTIONS,
  MARITIME_INCOTERMS,
} from "../../constants/shipmentFormConstants";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

interface GeneralInformationProps {
  register: UseFormRegister<CreateShipmentData>;
  watch: UseFormWatch<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  shipment?: Shipment;
  isEditMode: boolean;
  isAssignedToDriverApp: boolean;
}

export const GeneralInformation: React.FC<GeneralInformationProps> = ({
  register,
  watch,
  errors,
  getErrorMessage,
}) => {
  const { t } = useTranslation("shipments");
  const shipmentType = [
    { label: t("general.internationalCmr"), value: "International" },
    { label: t("general.domesticInland"), value: "Domestic" },
  ];

  const transportMode = watch("transportMode") || "ROAD";

  // FAS / FOB / CFR / CIF are maritime-only — only offered for SEA.
  const incotermOptions = useMemo(
    () =>
      transportMode === "SEA"
        ? INCOTERMS_OPTIONS
        : INCOTERMS_OPTIONS.filter(
            (option) => !MARITIME_INCOTERMS.includes(option.value),
          ),
    [transportMode],
  );

  return (
    <SectionCard icon={FileText} title={t("general.title")} className="basis-1/2">
      <div className="space-y-1">
        {/* Type only applies to Road shipments (International CMR / Domestic).
            Sea / Air are handled by their own detail sections, so the field is
            hidden — transportMode is now selected in the form header. */}
        {transportMode === "ROAD" && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.shipmentType")}
            error={getErrorMessage(errors.type)}
          >
            <Select
              {...register("type")}
              options={shipmentType}
              error={!!errors.type}
              size="sm"
            />
          </FormField>
        )}

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.reference")}
          error={getErrorMessage(errors.referenceNumber)}
        >
          <Input
            {...register("referenceNumber")}
            type="text"
            error={!!errors.referenceNumber}
            placeholder={t("general.referenceNumber")}
            size="sm"
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.incotermPlace")}
        >
          <div className="flex border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent overflow-hidden">
            <Select
              {...register("incoterms")}
              options={incotermOptions}
              placeholder={t("general.incoterm")}
              size="sm"
              className="max-w-[110px] border-0 bg-gray-50 focus:ring-0 rounded-none"
            />
            <div className="w-px bg-gray-300" />
            <Input
              {...register("place")}
              type="text"
              placeholder={t("general.place")}
              size="sm"
              className="flex-1 border-0 focus:ring-0 rounded-none"
            />
          </div>
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.direction")}
        >
          <Select
            {...register("direction")}
            size="sm"
            options={[
              { value: "", label: t("general.direction") },
              { value: "Inbound", label: t("general.inbound") },
              { value: "Outbound", label: t("general.outbound") },
            ]}
          />
        </FormField>
      </div>
    </SectionCard>
  );
};
